import { useState, useEffect, useMemo } from 'react';
import { api, ApiError } from '../lib/api';
import { PromoTypeResponse, CreatePromoType } from '@leads-platform/shared';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { SearchInput } from '../components/ui/search-input';
import { Select } from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { useToast } from '../components/ui/toast';

const STATUSES = { active: 'Active', inactive: 'Inactive' } as const;
type PromoStatus = keyof typeof STATUSES;

function toPromoStatus(value: string): PromoStatus {
  return value === 'inactive' ? 'inactive' : 'active';
}

interface FormData {
  slug: string;
  name: string;
  status: PromoStatus;
}

const initialFormState: FormData = {
  slug: '',
  name: '',
  status: 'active',
};

/**
 * Search over the slug (the `utm_campaign` value) and the display name.
 * Same idiom as the Sites screen.
 */
function searchBlob(p: PromoTypeResponse): string {
  return [p.slug, p.name].filter(Boolean).join(' ').toLowerCase();
}

/**
 * Ops-managed promo types — the CTO link's `utm_campaign` picklist
 * (admin-requirements §2.9). Cloned from the Sites screen: list with search,
 * dialog add/edit, delete with confirm. A duplicate slug (409) surfaces as a
 * form error inside the dialog.
 */
export function PromoTypes() {
  const [promoTypes, setPromoTypes] = useState<PromoTypeResponse[]>([]);
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormState);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { success, error: toastError } = useToast();

  const loadPromoTypes = () => {
    api
      .getPromoTypes()
      .then(setPromoTypes)
      .catch((err: ApiError) => setError(err.message));
  };

  useEffect(() => {
    loadPromoTypes();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);

    try {
      if (editingId) {
        await api.updatePromoType(editingId, {
          slug: formData.slug.trim().toLowerCase(),
          name: formData.name,
          status: formData.status,
        });
        success('Promo type updated successfully');
      } else {
        const data: CreatePromoType = {
          slug: formData.slug.trim().toLowerCase(),
          name: formData.name,
        };
        await api.createPromoType(data);
        success('Promo type created successfully');
      }

      setIsDialogOpen(false);
      setFormData(initialFormState);
      setEditingId(null);
      loadPromoTypes();
    } catch (err) {
      const apiError = err as ApiError;
      const fieldErrors = apiError.validationErrors
        ? Object.values(apiError.validationErrors).flat().join(' ')
        : '';
      setFormError([apiError.message, fieldErrors].filter(Boolean).join(': '));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (promoType: PromoTypeResponse) => {
    setEditingId(promoType.id);
    setFormError(null);
    setFormData({
      slug: promoType.slug,
      name: promoType.name,
      status: toPromoStatus(promoType.status),
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to delete "${name}"?`)) return;

    try {
      await api.deletePromoType(id);
      success('Promo type deleted successfully');
      loadPromoTypes();
    } catch (err) {
      const apiError = err as ApiError;
      toastError(apiError.message || 'Failed to delete promo type');
    }
  };

  const openCreateDialog = () => {
    setEditingId(null);
    setFormData(initialFormState);
    setFormError(null);
    setIsDialogOpen(true);
  };

  // Client-side keyword filter over slug + name
  const filteredPromoTypes = useMemo(() => {
    const needle = q.trim().toLowerCase();

    return promoTypes.filter((p) => {
      if (needle && !searchBlob(p).includes(needle)) return false;
      return true;
    });
  }, [promoTypes, q]);

  if (error) {
    return (
      <div>
        <div className="bg-destructive/10 text-destructive p-4 rounded-md">
          Error loading promo types: {error}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Promo Types</h1>
          <p className="text-muted-foreground mt-2">
            Manage the promo types offered on CTO links (the utm_campaign values)
          </p>
        </div>
        <Button onClick={openCreateDialog}>Create Promo Type</Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>All Promo Types</CardTitle>
            <SearchInput value={q} onChange={setQ} placeholder="Search slug or name" />
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Slug</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {promoTypes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                    No promo types found. Create your first promo type to get started.
                  </TableCell>
                </TableRow>
              ) : filteredPromoTypes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                    No promo types match your search.
                  </TableCell>
                </TableRow>
              ) : (
                filteredPromoTypes.map((promoType) => (
                  <TableRow key={promoType.id}>
                    <TableCell className="font-mono">{promoType.slug}</TableCell>
                    <TableCell className="font-medium">{promoType.name}</TableCell>
                    <TableCell className="text-muted-foreground">{promoType.status}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(promoType)}
                        className="mr-2"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(promoType.id, promoType.name)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Promo Type' : 'Create Promo Type'}</DialogTitle>
            <DialogDescription>
              {editingId
                ? 'Update the promo type details below.'
                : 'Create a new promo type by filling in the details below.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              {formError && <p className="text-sm text-destructive">{formError}</p>}
              <div className="space-y-2">
                <Label htmlFor="slug">Slug</Label>
                <Input
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  required
                  placeholder="e.g., 7-high-yield"
                  pattern="[a-z0-9]+(-[a-z0-9]+)*"
                  title="Slug must be lowercase letters, numbers, and hyphens"
                />
                <p className="text-xs text-muted-foreground">
                  Used as the utm_campaign value on CTO links. Lowercase letters, numbers and
                  dashes.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., 7 High Yield"
                />
              </div>
              {editingId && (
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    id="status"
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: toPromoStatus(e.target.value) })
                    }
                  >
                    {Object.entries(STATUSES).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Saving...' : editingId ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
